import React, { useState, useEffect, useCallback } from "react";
import {
  FaMoneyBillWave, FaChevronDown, FaChevronUp, FaEdit,
  FaCheckCircle, FaExclamationTriangle, FaSave, FaUserTie,
} from "react-icons/fa";
import {useTheme, getT, getBrandColors, lighten} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, -apple-system, sans-serif";
const BLUR = "blur(18px)";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function makeS(T) {
  return {
    card:  { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, border: T.glassBdr, borderRadius: 18, boxShadow: T.glassSh, padding: "24px 28px", boxSizing: "border-box" },
    inp:   { padding: "9px 13px", borderRadius: 9, border: `1.5px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, fontSize: 14, fontFamily: FONT, outline: "none" },
    yBtn:  { padding: "11px 24px", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", color: getBrandColors().blueDark, background: "linear-gradient(135deg,#ffd700,#fff3a0)", boxShadow: "0 4px 14px rgba(255,200,0,0.32)", display: "flex", alignItems: "center", gap: 8, fontFamily: FONT },
    gBtn:  { padding: "9px 18px", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", color: "#fff", background: `linear-gradient(135deg,${lighten(getBrandColors().green,20)},${getBrandColors().green})`, boxShadow: "0 3px 10px rgba(16,185,129,0.28)", display: "flex", alignItems: "center", gap: 7, fontFamily: FONT },
    lbl:   { fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textLabel, fontFamily: FONT, marginBottom: 5, display: "block" },
  };
}

const fmt$ = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtH = (v) => `${Number(v || 0).toFixed(2)}h`;

// ─── KPI Tile ─────────────────────────────────────────────────────────────────
function KpiTile({ label, value, color, T }) {
  return (
    <div style={{ flex: 1, minWidth: 140, padding: "18px 20px", borderRadius: 14,
      background: `rgba(${color},0.08)`, border: `1.5px solid rgba(${color},0.2)` }}>
      <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
        color: `rgb(${color})`, fontFamily: FONT, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>{value}</div>
    </div>
  );
}

// ─── Rate Editor ──────────────────────────────────────────────────────────────
function RateEditor({ coachId, label, currentRate, field, onSaved, T, S }) {
  const [editing, setEditing] = useState(false);
  const [val,     setVal]     = useState(String(currentRate));
  const [saving,  setSaving]  = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      let url, body;
      if (field === "group") {
        url  = `${BASE}/coach-pay/rates/${coachId}`;
        body = { hourly_rate: parseFloat(val) || 0 };
      } else {
        url  = `${BASE}/oto/coaches/${coachId}/rate`;
        body = { hourly_rate: parseFloat(val) || 0 };
      }
      const r = await fetch(url, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error("Save failed");
      onSaved(parseFloat(val) || 0);
      setEditing(false);
    } catch { }
    finally { setSaving(false); }
  };

  return editing ? (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT }}>$</span>
      <input type="number" min="0" step="0.5" value={val}
        onChange={e => setVal(e.target.value)}
        style={{ ...S.inp, width: 80 }} />
      <button style={{ ...S.gBtn, padding: "6px 12px", fontSize: 12 }} onClick={save} disabled={saving}>
        <FaSave size={11} />{saving ? "…" : "Save"}
      </button>
      <button onClick={() => setEditing(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, fontFamily: FONT, fontSize: 12 }}>✕</button>
    </div>
  ) : (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: FONT }}>{fmt$(currentRate)}/hr</span>
      <button onClick={() => { setVal(String(currentRate)); setEditing(true); }}
        style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 2 }}>
        <FaEdit size={12} />
      </button>
    </div>
  );
}

// ─── Daily Log Expandable ─────────────────────────────────────────────────────
function DailyLog({ logs, T }) {
  const { fmtDate } = useDateTimeFormat();
  const [open, setOpen] = useState(false);
  const { sorted: sortedLogs, toggleSort, sortArrow } = useSort(logs || [], "log_date", "desc");

  if (!logs || !logs.length) return (
    <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT }}>No clock-in records this month.</div>
  );

  const thSt = { padding: "9px 12px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: T.textLabel, fontFamily: FONT, textAlign: "left", whiteSpace: "nowrap" };
  const tdSt = { padding: "10px 12px", fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FONT, borderTop: `1px solid ${T.divider}` };
  const thSortSt = { ...thSt, cursor: "pointer", userSelect: "none" };

  return (
    <div>
      <button onClick={() => setOpen(o => !o)}
        style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          fontSize: 13, fontWeight: 700, color: "#6366f1", fontFamily: FONT, padding: 0, marginBottom: open ? 12 : 0 }}>
        {open ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
        {open ? "Hide" : "Show"} daily breakdown ({logs.length} day{logs.length !== 1 ? "s" : ""})
      </button>
      {open && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thSortSt} onClick={() => toggleSort("log_date")}>Date{sortArrow("log_date")}</th>
                <th style={thSortSt} onClick={() => toggleSort("clockin_time")}>Clock In{sortArrow("clockin_time")}</th>
                <th style={thSortSt} onClick={() => toggleSort("group_session_start")}>Group Session Start{sortArrow("group_session_start")}</th>
                <th style={thSortSt} onClick={() => toggleSort("effective_group_start")}>Effective Start{sortArrow("effective_group_start")}</th>
                <th style={thSortSt} onClick={() => toggleSort("clockout_time")}>Clock Out{sortArrow("clockout_time")}</th>
                <th style={thSortSt} onClick={() => toggleSort("group_hours")}>Group Hrs{sortArrow("group_hours")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedLogs.map((l, i) => {
                const late = l.clockin_time > l.group_session_start;
                return (
                  <tr key={i}>
                    <td style={tdSt}>{fmtDate(l.log_date)}</td>
                    <td style={tdSt}>{l.clockin_time}</td>
                    <td style={{ ...tdSt, color: T.textMuted }}>{l.group_session_start}</td>
                    <td style={{ ...tdSt, color: late ? "#f59e0b" : "#10b981", fontWeight: 800 }}>
                      {l.effective_group_start}
                      {late && <span style={{ fontSize: 10, marginLeft: 6, color: "#f59e0b" }}>late</span>}
                    </td>
                    <td style={tdSt}>{l.clockout_time}</td>
                    <td style={{ ...tdSt, fontWeight: 800, color: "#6366f1" }}>{fmtH(l.group_hours)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Coach Row ────────────────────────────────────────────────────────────────
function CoachRow({ coach, onRateUpdate, T, S }) {
  const [expanded, setExpanded] = useState(false);
  const [groupRate, setGroupRate] = useState(coach.group_rate);
  const [otoRate,   setOtoRate]   = useState(coach.oto_rate);

  const variance     = coach.variance_hrs;
  const varColor     = variance > 0 ? "#f59e0b" : variance < 0 ? "#ef4444" : "#10b981";
  const varLabel     = variance > 0 ? `${fmtH(Math.abs(variance))} under` : variance < 0 ? `${fmtH(Math.abs(variance))} over` : "On target";

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      {/* Coach header row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        {/* Avatar */}
        <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(99,102,241,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FaUserTie size={18} color="#6366f1" />
        </div>

        {/* Name + rates */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: T.text, fontFamily: FONT, marginBottom: 8 }}>{coach.coach_name}</div>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, color: T.textLabel, fontFamily: FONT, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Group Rate</div>
              <RateEditor coachId={coach.coach_id} label="Group" currentRate={groupRate} field="group"
                onSaved={v => { setGroupRate(v); onRateUpdate(coach.coach_id, "group", v); }} T={T} S={S} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: T.textLabel, fontFamily: FONT, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>1-on-1 Rate</div>
              <RateEditor coachId={coach.coach_id} label="1-on-1" currentRate={otoRate} field="oto"
                onSaved={v => { setOtoRate(v); onRateUpdate(coach.coach_id, "oto", v); }} T={T} S={S} />
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
          <StatPill label="Days" value={coach.days_worked} color="99,102,241" T={T} />
          <StatPill label="Planned" value={fmtH(coach.planned_hrs)} color="14,165,233" T={T} />
          <StatPill label="Actual Group" value={fmtH(coach.actual_group_hrs)} color="99,102,241" T={T} />
          <StatPill label="Variance" value={varLabel} color={variance === 0 ? "16,185,129" : variance > 0 ? "245,158,11" : "239,68,68"} T={T} />
          <StatPill label="1-on-1" value={fmtH(coach.oto_hrs)} color="14,165,233" T={T} />
          <StatPill label="Group Pay" value={fmt$(coach.group_pay)} color="16,185,129" T={T} bold />
          <StatPill label="1-on-1 Pay" value={fmt$(coach.oto_pay)} color="16,185,129" T={T} bold />
          <StatPill label="Total Pay" value={fmt$(coach.total_pay)} color="16,185,129" T={T} bold large />
        </div>

        {/* Expand toggle */}
        <button onClick={() => setExpanded(e => !e)}
          style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 4, alignSelf: "flex-start" }}>
          {expanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
        </button>
      </div>

      {/* Daily log */}
      {expanded && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.divider}` }}>
          <DailyLog logs={coach.daily_logs} T={T} />
        </div>
      )}
    </div>
  );
}

function StatPill({ label, value, color, T, bold, large }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: T.textLabel, fontFamily: FONT, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: large ? 18 : 15, fontWeight: bold || large ? 800 : 700,
        color: large ? `rgb(${color})` : T.text, fontFamily: FONT }}>{value}</div>
    </div>
  );
}

// ─── Totals Bar ───────────────────────────────────────────────────────────────
function TotalsBar({ totals, T }) {
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
      <KpiTile label="Planned Group Hrs"  value={fmtH(totals.planned_hrs)}      color="14,165,233"  T={T} />
      <KpiTile label="Actual Group Hrs"   value={fmtH(totals.actual_group_hrs)} color="99,102,241"  T={T} />
      <KpiTile label="1-on-1 Hrs"         value={fmtH(totals.oto_hrs)}          color="14,165,233"  T={T} />
      <KpiTile label="Group Pay"          value={fmt$(totals.group_pay)}        color="16,185,129"  T={T} />
      <KpiTile label="1-on-1 Pay"         value={fmt$(totals.oto_pay)}          color="16,185,129"  T={T} />
      <KpiTile label="Total Payroll"      value={fmt$(totals.total_pay)}        color="245,158,11"  T={T} />
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function CoachPaymentReport() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const S = makeS(T);

  const now    = new Date();
  const [month,   setMonth]   = useState(now.getMonth() + 1);
  const [year,    setYear]    = useState(now.getFullYear());
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/coach-pay/report?month=${month}&year=${year}`)
      .then(r => r.json())
      .then(d => { setReport(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const handleRateUpdate = (coachId, field, val) => {
    // Optimistically update totals by reloading
    load();
  };

  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
        <PageHeaderIcon icon={<FaMoneyBillWave size={20} color={getBrandColors().blueDark} />} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>Coach Payment Report</div>
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
            Monthly payroll — group coaching vs 1-on-1 coaching hours and pay
          </div>
        </div>
      </div>

      {/* Month / Year selector */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 28, flexWrap: "wrap" }}>
        <div>
          <label style={S.lbl}>Month</label>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...S.inp, width: 150 }}>
            {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label style={S.lbl}>Year</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...S.inp, width: 100 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button style={S.yBtn} onClick={load} disabled={loading}>
          {loading ? "Loading…" : `View ${MONTH_NAMES[month - 1]} ${year}`}
        </button>
      </div>

      {toast && (
        <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: FONT,
          background: toast.ok ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
          color: toast.ok ? getBrandColors().green : getBrandColors().red,
          border: toast.ok ? "1px solid rgba(52,211,153,0.3)" : "1px solid rgba(248,113,113,0.3)" }}>
          {toast.msg}
        </div>
      )}

      {loading && (
        <div style={{ color: T.textMuted, fontFamily: FONT, fontSize: 14, padding: 40, textAlign: "center" }}>Loading report…</div>
      )}

      {!loading && report && (
        <>
          {/* Planned vs Actual callout */}
          <div style={{ marginBottom: 24, padding: "14px 20px", borderRadius: 12,
            background: "rgba(99,102,241,0.08)", border: "1.5px solid rgba(99,102,241,0.2)" }}>
            <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, lineHeight: 1.6 }}>
              <strong style={{ color: T.text }}>Planned vs Actual:</strong> Planned hours come from the approved Monthly Planner.
              Actual group hours are calculated from clock-in records — if a coach arrived before their group session,
              hours are counted from session start, not from their early arrival.
              Positive variance = under-delivered; Negative = over-delivered.
            </div>
          </div>

          {/* Totals KPI bar */}
          {report.totals && <TotalsBar totals={report.totals} T={T} />}

          {/* Per-coach cards */}
          {(report.coaches || []).length === 0 ? (
            <div style={{ textAlign: "center", color: T.textMuted, fontFamily: FONT, fontSize: 14, padding: 60 }}>
              No coach data found for {MONTH_NAMES[month - 1]} {year}.
            </div>
          ) : (
            (report.coaches || []).map(coach => (
              <CoachRow
                key={coach.coach_id}
                coach={coach}
                onRateUpdate={handleRateUpdate}
                T={T} S={S}
              />
            ))
          )}
        </>
      )}
    </div>
  );
}
